import { REGEX } from '@/lib/constants/common';

const NANP_COUNTRY_CODES = new Set(['US', 'CA']);

// US and Canada share the NANP 10-digit format.
export function usesNanpPhoneFormat(countryCode?: string | null): boolean {
  return NANP_COUNTRY_CODES.has((countryCode ?? 'US').toUpperCase());
}

function shouldUseInternationalPhoneNormalization(
  input: string,
  countryCode?: string | null,
): boolean {
  if (!usesNanpPhoneFormat(countryCode)) {
    return true;
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith('+')) {
    return true;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) {
    return false;
  }

  const core = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return core.length !== 10;
}

function normalizeInternationalPhoneNumber(
  input: string,
  mode: 'pretty' | 'digits' | 'e164',
): string {
  const trimmed = input.trim();
  const digits = trimmed.replace(/\D/g, '');

  if (digits.length < 7 || digits.length > 15) {
    return '';
  }

  if (mode === 'digits') {
    return digits;
  }

  if (mode === 'e164') {
    return trimmed.startsWith('+') ? `+${digits}` : '';
  }

  return trimmed;
}

export function isValidPhoneNumber(phone: string, countryCode?: string | null): boolean {
  const trimmed = phone.trim();
  if (!trimmed) {
    return false;
  }

  if (shouldUseInternationalPhoneNormalization(trimmed, countryCode)) {
    if (!/^\+?[\d\s().-]+$/.test(trimmed)) {
      return false;
    }

    const digits = trimmed.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }

  return REGEX.PHONE.test(trimmed);
}

/**
 * Normalizes a phone number for checkout using the billing/shipping country.
 */
export function normalizePhoneNumber(
  input: string,
  countryCode?: string | null,
  mode: 'pretty' | 'digits' | 'e164' = 'digits',
): string {
  if (shouldUseInternationalPhoneNormalization(input, countryCode)) {
    return normalizeInternationalPhoneNumber(input, mode);
  }

  return formatPhoneInput(input, mode);
}

/**
 * Formats/normalizes US phone numbers from messy input.
 *
 * - Accepts optional +1/1 and punctuation/whitespace.
 * - Valid only if there are exactly 10 digits after stripping an optional leading 1.
 * - Returns "" if invalid.
 *
 * mode:
 *  - "pretty" (default): "123-456-7890"
 *  - "digits":           "1234567890"
 *  - "e164":             "+11234567890"
 */
export function formatPhoneInput(
  input: string,
  mode: "pretty" | "digits" | "e164" = "pretty"
): string {
  if (typeof input !== "string") return "";

  // strip everything but digits
  const digits = input.replace(/\D/g, "");

  // drop optional US country code
  const core = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;

  if (core.length !== 10) {
    return "";
  }

  if (mode === "digits") return core;
  if (mode === "e164") return `+1${core}`;
  // default 'pretty' (backward compatible with your original helper)
  return `${core.slice(0, 3)}-${core.slice(3, 6)}-${core.slice(6)}`;
}
